import { useMutation, useQuery, useQueryClient } from 'react-query'
import { IconButton, Stack, useMediaQuery, useTheme } from '@mui/material'
import FilterListRoundedIcon from '@mui/icons-material/FilterListRounded'
import { Button, DeleteIcon } from 'components'
import { CrudAction, Entity, usePermissions } from 'utils/hooks'
import { ListResponseType } from 'services/types'
import useDiscardResultsModal from 'pages/Shipment/pages/IncomingDeliveriesDetails/dialogs/useDiscardResultsModal'
import { useBakersService } from 'services/bakersService'
import { successToast } from 'utils/toastService'
import { useBakersRecommendationService } from 'services/bakerRecomendationService'
import { useBakersInsightService } from 'services/bakersInsightService'
import { TBakersRecommendation } from 'types/models/BakersRecommendations'
import { TBakersInsight } from 'types/models/BakersInsights'
import { CardWithTitle } from './components/CardWithTitle'
import { DeliveryErrorState } from './components/DeliveryErrorState'
import { DeliveryEmptyState } from './components/DeliveryEmptyState'
import { AnalysisAttributesSection } from './sections/AnalysisAttributesSection'
import { AnalysisSpectralDistanceSection } from './sections/AnalysisSpectralDistanceSection'
import { AnalysisConsistencySection } from './sections/AnalysisConsistencySection'
import { DeliveryLoadingState } from './components/DeliveryLoadingState'
import { InsightsAndRecommendations } from './sections/recommendationsInsights/InsightsAndRecommendations'
import { useAnalyticsServices } from '../services/services'
import useFiltersModal from '../../dialogs/useFiltersModal'
import * as sxStyles from './AnalyticsDetails.styled'

interface IFlourAnalysisProps {
  flourProductId: number
  selectedDeliveryId?: string
  isPostAging: boolean
  isBakerModule: boolean
}

export const AnalysisResults = ({
  flourProductId,
  selectedDeliveryId,
  isPostAging,
  isBakerModule,
}: IFlourAnalysisProps) => {
  const theme = useTheme()
  const { checkPermission } = usePermissions()
  const queryClient = useQueryClient()
  const isXXSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const { getUserAttributeFilters, getProductDeliveryById } =
    useAnalyticsServices(isBakerModule)
  const { setBakersDeliveryAsDiscarded } = useBakersService()
  const { getBakersRecommendationListByDeliveryId } =
    useBakersRecommendationService()
  const { getBakersInsightListByDeliveryId } = useBakersInsightService()

  const { data: userAttributeFilters } = useQuery({
    queryKey: ['flour_attributes_user_filter'],
    queryFn: () => getUserAttributeFilters(isPostAging, flourProductId),
    retry: false,
  })

  const deliveryQueryKey = ['delivery-by-id', selectedDeliveryId] as const

  const {
    data: deliveryResponse,
    isLoading: isDeliveryLoading,
    isError: isDeliveryError,
  } = useQuery(
    deliveryQueryKey,
    () => getProductDeliveryById(selectedDeliveryId!),
    {
      enabled: !!selectedDeliveryId,
      keepPreviousData: true,
    }
  )

  const bakerRecommendationsQueryKey = [
    'bakerRecommendations',
    selectedDeliveryId,
  ]

  const { data: recommendationsResponse, isLoading: isRecommendationsLoading } =
    useQuery<ListResponseType<TBakersRecommendation>>(
      bakerRecommendationsQueryKey,
      () =>
        getBakersRecommendationListByDeliveryId(Number(selectedDeliveryId)!),
      {
        enabled: !!selectedDeliveryId,
        keepPreviousData: true,
      }
    )

  const bakerInsightsQueryKey = ['bakerInsights', selectedDeliveryId]

  const { data: insightsResponse, isLoading: isInsightsLoading } = useQuery<
    ListResponseType<TBakersInsight>
  >(
    bakerInsightsQueryKey,
    () => getBakersInsightListByDeliveryId(Number(selectedDeliveryId)!),
    {
      enabled: !!selectedDeliveryId,
      keepPreviousData: true,
    }
  )

  const delivery = deliveryResponse?.result

  const [filtersModal, toggleFiltersModal] = useFiltersModal({
    userAttributeFilters: userAttributeFilters,
    post_aging: isPostAging,
    isBakerModule: isBakerModule,
  })

  const { mutate: discardDeliveryMutation, isLoading: discardDeliveryLoading } =
    useMutation(
      (deliveryId: number) => setBakersDeliveryAsDiscarded(deliveryId),
      {
        onSuccess: (data) => {
          if (data?.status === true) {
            successToast('Baker delivery discarded successfully')
            queryClient.invalidateQueries(deliveryQueryKey)
            queryClient.invalidateQueries([
              'deliveries-by-date',
              String(flourProductId),
            ])
          }
        },
      }
    )

  const [discardModal, toggleDiscardModal] = useDiscardResultsModal({
    onConfirm: () => {
      discardDeliveryMutation(delivery?.id)
    },
  })

  const filtersIcon = () => (
    <IconButton onClick={toggleFiltersModal}>
      <FilterListRoundedIcon />
    </IconButton>
  )

  if (!selectedDeliveryId) {
    return <DeliveryEmptyState />
  }

  if (isDeliveryLoading) {
    return <DeliveryLoadingState />
  }

  if (isDeliveryError || !delivery) {
    return <DeliveryErrorState />
  }

  return (
    <Stack sx={sxStyles.analysisContainer}>
      <Stack gap={1} flexDirection="row" overflow="auto" minHeight="800px">
        <Stack gap={1} flex={4}>
          <Stack>
            <CardWithTitle
              title="Analysis Results"
              subTitle={delivery?.name}
              icon={filtersIcon()}
            />
          </Stack>
          <Stack sx={sxStyles.analysisWrapper}>
            <Stack
              sx={sxStyles.analysisStatusSection}
              flexDirection={isXXSmallScreen ? 'column' : 'row'}
            >
              <AnalysisSpectralDistanceSection
                selectedDeliveryId={selectedDeliveryId}
                isPostAging={isPostAging}
                isBakerModule={isBakerModule}
              />
              <AnalysisConsistencySection
                delivery={delivery}
                isDeliveryLoading={isDeliveryLoading}
              />
            </Stack>
            <AnalysisAttributesSection
              attributes={delivery?.attributes}
              genericAttributes={userAttributeFilters}
            />
            {isBakerModule && (
              <Stack sx={sxStyles.discardButtonStack}>
                <Button
                  disabled={
                    discardDeliveryLoading
                      ? true
                      : delivery?.discarded
                        ? true
                        : !checkPermission(
                            Entity.BAKER_ANALYTICS,
                            CrudAction.UPDATE
                          )
                  }
                  onClick={toggleDiscardModal}
                  variantType="secondary"
                  startIcon={<DeleteIcon size={20} />}
                >
                  {delivery?.discarded ? 'Discarded' : 'Discard Results'}
                </Button>
              </Stack>
            )}
          </Stack>
        </Stack>
        {isBakerModule && (
          <InsightsAndRecommendations
            recommendations={recommendationsResponse}
            isRecommendationsLoading={isRecommendationsLoading}
            insights={insightsResponse}
            isInsightsLoading={isInsightsLoading}
            selectedDeliveryId={selectedDeliveryId}
            moduleKey={'bakers'}
          />
        )}
      </Stack>
      {filtersModal}
      {discardModal}
    </Stack>
  )
}
